import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';

const LORE_CATEGORIES = ["History", "Factions", "Technology", "Species", "Locations", "Events", "General"];

function Admin() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [downloading, setDownloading] = useState(false);
  
  // Element value state
  const [elementValues, setElementValues] = useState(null);
  const [rarityMultipliers, setRarityMultipliers] = useState({});
  const [savingValues, setSavingValues] = useState(false);
  const [resettingValues, setResettingValues] = useState(false);

  // Colonization chance state
  const [colonizationChance, setColonizationChance] = useState(0.02);
  const [colonizationDefault, setColonizationDefault] = useState(0.02);
  const [savingColonization, setSavingColonization] = useState(false);

  // Lore state
  const [loreEntries, setLoreEntries] = useState([]);
  const [loreLoading, setLoreLoading] = useState(false);
  const [newLore, setNewLore] = useState({ title: '', category: 'General', content: '', use_in_systems: true, use_in_planets: true });
  const [savingLore, setSavingLore] = useState(false);
  const [editingLore, setEditingLore] = useState(null);
  const [lorePreview, setLorePreview] = useState(null);
  const [showLorePreview, setShowLorePreview] = useState(false);

  useEffect(() => {
    // Redirect if not admin
    if (user && !user.is_admin) {
      navigate('/explore/octants');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.is_admin) {
      fetchStats();
      fetchElementValues();
      fetchColonizationChance();
      fetchLore();
    }
  }, [user]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchElementValues = async () => {
    try {
      const response = await api.get('/api/elements/values');
      setElementValues(response.data);
      setRarityMultipliers(response.data.rarity_multipliers || {});
    } catch (error) {
      console.error('Failed to fetch element values:', error);
    }
  };

  const fetchColonizationChance = async () => {
    try {
      const response = await api.get('/api/admin/settings/colonization-chance');
      setColonizationChance(response.data.colonization_chance);
      setColonizationDefault(response.data.default);
    } catch (error) {
      console.error('Failed to fetch colonization chance:', error);
    }
  };

  const fetchLore = async () => {
    setLoreLoading(true);
    try {
      const response = await api.get('/api/admin/lore');
      setLoreEntries(response.data.lore_entries || []);
    } catch (error) {
      console.error('Failed to fetch lore:', error);
    } finally {
      setLoreLoading(false);
    }
  };

  const handleSaveColonizationChance = async () => {
    setSavingColonization(true);
    try {
      await api.post('/api/admin/settings/colonization-chance', {
        percentage: colonizationChance
      });
      alert(`Colonization chance updated to ${colonizationChance}%`);
    } catch (error) {
      console.error('Failed to save colonization chance:', error);
      alert(error.response?.data?.detail || 'Failed to save colonization chance');
    } finally {
      setSavingColonization(false);
    }
  };

  const handleCreateLore = async () => {
    if (!newLore.title.trim() || !newLore.content.trim()) {
      alert('Title and content are required');
      return;
    }

    setSavingLore(true);
    try {
      await api.post('/api/admin/lore', newLore);
      setNewLore({ title: '', category: 'General', content: '', use_in_systems: true, use_in_planets: true });
      fetchLore();
      alert('Lore entry created successfully');
    } catch (error) {
      console.error('Failed to create lore:', error);
      alert(error.response?.data?.detail || 'Failed to create lore entry');
    } finally {
      setSavingLore(false);
    }
  };

  const handleUpdateLore = async (loreId) => {
    if (!editingLore) return;

    setSavingLore(true);
    try {
      await api.put(`/api/admin/lore/${loreId}`, editingLore);
      setEditingLore(null);
      fetchLore();
      alert('Lore entry updated successfully');
    } catch (error) {
      console.error('Failed to update lore:', error);
      alert(error.response?.data?.detail || 'Failed to update lore entry');
    } finally {
      setSavingLore(false);
    }
  };

  const handleDeleteLore = async (loreId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;

    try {
      await api.delete(`/api/admin/lore/${loreId}`);
      fetchLore();
      alert('Lore entry deleted');
    } catch (error) {
      console.error('Failed to delete lore:', error);
      alert(error.response?.data?.detail || 'Failed to delete lore entry');
    }
  };

  const handlePreviewLore = async () => {
    try {
      const response = await api.get('/api/admin/lore/preview');
      setLorePreview(response.data);
      setShowLorePreview(true);
    } catch (error) {
      console.error('Failed to preview lore:', error);
      alert(error.response?.data?.detail || 'Failed to preview lore');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'History': '#F59E0B',
      'Factions': '#EF4444',
      'Technology': '#3B82F6',
      'Species': '#10B981',
      'Locations': '#8B5CF6',
      'Events': '#EC4899',
      'General': '#6B7280'
    };
    return colors[category] || '#6B7280';
  };

  const handleResetUniverse = async () => {
    if (!confirmReset) {
      setConfirmReset(true);
      return;
    }

    setResetting(true);
    try {
      const response = await api.post('/api/admin/reset-universe');
      alert(response.data.message);
      setConfirmReset(false);
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Failed to reset universe:', error);
      alert(error.response?.data?.detail || 'Failed to reset universe');
    } finally {
      setResetting(false);
    }
  };

  const handleDownloadEmails = async () => {
    console.log('Download emails button clicked');
    setDownloading(true);
    try {
      console.log('Fetching users from /api/admin/users...');
      const response = await api.get('/api/admin/users');
      console.log('API response:', response.data);
      
      const users = response.data.users;
      
      if (!users || users.length === 0) {
        alert('No users found to download');
        setDownloading(false);
        return;
      }
      
      console.log(`Creating CSV for ${users.length} users...`);
      
      // Create CSV content
      const csvHeader = 'Email,Credits,Registration Date\n';
      const csvRows = users.map(u => 
        `${u.email},${u.credits},"${u.created_at || 'N/A'}"`
      ).join('\n');
      const csvContent = csvHeader + csvRows;
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      
      const filename = `arcunc-universe-users-${new Date().toISOString().split('T')[0]}.csv`;
      
      // Create and click download link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      console.log('Triggering download...');
      link.click();
      
      // Clean up after a short delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        console.log('Download cleanup complete');
      }, 100);
      
      console.log(`Successfully prepared ${users.length} user emails for download`);
    } catch (error) {
      console.error('Failed to download emails:', error);
      alert(error.response?.data?.detail || 'Failed to download user emails. Check console for details.');
    } finally {
      setDownloading(false);
    }
  };

  const handleRarityMultiplierChange = (rarity, value) => {
    setRarityMultipliers(prev => ({
      ...prev,
      [rarity]: parseFloat(value) || 0
    }));
  };

  const handleSaveRarityMultiplier = async (rarity) => {
    setSavingValues(true);
    try {
      await api.post('/api/admin/elements/rarity-multiplier', {
        rarity,
        multiplier: rarityMultipliers[rarity]
      });
      alert(`Updated ${rarity} multiplier to ${rarityMultipliers[rarity]} credits/kg`);
      fetchElementValues();
    } catch (error) {
      console.error('Failed to save rarity multiplier:', error);
      alert(error.response?.data?.detail || 'Failed to save multiplier');
    } finally {
      setSavingValues(false);
    }
  };

  const handleResetAllValues = async () => {
    if (!window.confirm('Are you sure you want to reset all element values to defaults? This cannot be undone.')) {
      return;
    }

    setResettingValues(true);
    try {
      const response = await api.post('/api/admin/elements/reset-all');
      alert(response.data.message);
      fetchElementValues();
    } catch (error) {
      console.error('Failed to reset element values:', error);
      alert(error.response?.data?.detail || 'Failed to reset values');
    } finally {
      setResettingValues(false);
    }
  };

  const getRarityColor = (rarity) => {
    const colors = {
      'Common': '#9CA3AF',
      'Uncommon': '#10B981',
      'Rare': '#3B82F6',
      'Ultra Rare': '#A855F7',
      'Legendary': '#F59E0B',
      'Mythic': '#EF4444'
    };
    return colors[rarity] || '#9CA3AF';
  };

  if (!user || !user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-card rounded-xl p-8">
          <h2 className="text-2xl font-bold text-destructive mb-4">Access Denied</h2>
          <p className="text-muted-foreground">You do not have admin privileges.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      <Header breadcrumbs={[{ label: 'Admin Dashboard' }]} />

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-4xl font-bold mb-2">Admin Dashboard</h2>
          <p className="text-muted-foreground">Universe management and element value controls</p>
        </div>

        {/* Statistics */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="glass-card rounded-xl p-6" data-testid="stat-systems">
                <div className="text-sm text-muted-foreground mb-2">Systems Discovered</div>
                <div className="text-3xl font-bold text-primary">{stats?.total_systems_discovered || 0}</div>
              </div>

              <div className="glass-card rounded-xl p-6" data-testid="stat-users">
                <div className="text-sm text-muted-foreground mb-2">Total Users</div>
                <div className="text-3xl font-bold text-cyan-400">{stats?.total_users || 0}</div>
              </div>

              <div className="glass-card rounded-xl p-6" data-testid="stat-scans">
                <div className="text-sm text-muted-foreground mb-2">Planet Scans</div>
                <div className="text-3xl font-bold text-green-400">{stats?.total_scans || 0}</div>
              </div>

              <div className="glass-card rounded-xl p-6" data-testid="stat-elements">
                <div className="text-sm text-muted-foreground mb-2">Known Elements</div>
                <div className="text-3xl font-bold text-yellow-400">318</div>
              </div>
            </div>

            {/* Top Discoverers */}
            {stats?.top_discoverers && stats.top_discoverers.length > 0 && (
              <div className="glass-card rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4">Top Discoverers</h3>
                <div className="space-y-2">
                  {stats.top_discoverers.map((discoverer, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-cyan-400/20 rounded-full flex items-center justify-center">
                          <span className="text-cyan-400 font-bold">{idx + 1}</span>
                        </div>
                        <span className="text-foreground">{discoverer.email}</span>
                      </div>
                      <span className="text-primary font-semibold">{discoverer.systems} systems</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Colonization Chance Control */}
            <div className="glass-card rounded-xl p-6" data-testid="colonization-control">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold">Human Colonization Chance</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Set the percentage chance that a newly discovered system is already colonized by humans.
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-muted-foreground">Default: {colonizationDefault}%</span>
                </div>
              </div>

              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-green-400">Colonization Chance</span>
                      <span className="text-lg font-bold text-green-400">{colonizationChance}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="10"
                      step="0.01"
                      value={colonizationChance}
                      onChange={(e) => setColonizationChance(parseFloat(e.target.value))}
                      className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-green-400"
                      data-testid="colonization-slider"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>0%</span>
                      <span>5%</span>
                      <span>10%</span>
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={colonizationChance}
                      onChange={(e) => setColonizationChance(parseFloat(e.target.value) || 0)}
                      className="w-24 px-3 py-2 bg-white/5 border border-green-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 text-foreground text-sm text-center"
                      data-testid="colonization-input"
                    />
                    <button
                      onClick={handleSaveColonizationChance}
                      disabled={savingColonization}
                      className="px-4 py-2 bg-green-500/90 hover:bg-green-400 text-slate-900 font-semibold rounded-lg transition-all text-sm disabled:opacity-50"
                      data-testid="save-colonization-button"
                    >
                      {savingColonization ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-4 bg-green-400/10 rounded-lg border border-green-400/20">
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-muted-foreground">
                    <p className="mb-1"><strong className="text-foreground">How colonization works:</strong></p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>When a user visits a new system, there's a {colonizationChance}% chance it's colonized</li>
                      <li>Colonized systems have unique AI-generated histories about human settlements</li>
                      <li>This affects only newly discovered systems, not existing ones</li>
                      <li>At 0.02%, roughly 1 in 5,000 systems will be colonized</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Universe Lore Management */}
            <div className="glass-card rounded-xl p-6" data-testid="lore-management">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold">Universe Lore</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Define lore that will be incorporated into AI-generated system histories and planet scans.
                  </p>
                </div>
                <button
                  onClick={handlePreviewLore}
                  className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-purple-400/20 text-foreground rounded-lg transition-all text-sm"
                  data-testid="preview-lore-button"
                >
                  Preview AI Context
                </button>
              </div>

              {/* Add New Lore Form */}
              <div className="p-4 bg-white/5 rounded-lg border border-purple-400/20 mb-6">
                <h4 className="text-sm font-semibold text-purple-400 mb-3">Add New Lore Entry</h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={newLore.title}
                      onChange={(e) => setNewLore({ ...newLore, title: e.target.value })}
                      placeholder="Title (e.g., 'The Voidborn Faction')"
                      className="px-3 py-2 bg-white/5 border border-purple-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-foreground text-sm"
                      data-testid="lore-title-input"
                    />
                    <select
                      value={newLore.category}
                      onChange={(e) => setNewLore({ ...newLore, category: e.target.value })}
                      className="px-3 py-2 bg-white/5 border border-purple-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-foreground text-sm"
                      data-testid="lore-category-select"
                    >
                      {LORE_CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <textarea
                    value={newLore.content}
                    onChange={(e) => setNewLore({ ...newLore, content: e.target.value })}
                    placeholder="Lore content (max 2000 characters). This will be included in AI prompts when generating systems and planets..."
                    rows={3}
                    maxLength={2000}
                    className="w-full px-3 py-2 bg-white/5 border border-purple-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-foreground text-sm resize-none"
                    data-testid="lore-content-input"
                  />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2 text-sm">
                        <input
                          type="checkbox"
                          checked={newLore.use_in_systems}
                          onChange={(e) => setNewLore({ ...newLore, use_in_systems: e.target.checked })}
                          className="rounded border-purple-400/20 text-purple-400 focus:ring-purple-400"
                        />
                        <span>Use in Systems</span>
                      </label>
                      <label className="flex items-center space-x-2 text-sm">
                        <input
                          type="checkbox"
                          checked={newLore.use_in_planets}
                          onChange={(e) => setNewLore({ ...newLore, use_in_planets: e.target.checked })}
                          className="rounded border-purple-400/20 text-purple-400 focus:ring-purple-400"
                        />
                        <span>Use in Planets</span>
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-muted-foreground">{newLore.content.length}/2000</span>
                      <button
                        onClick={handleCreateLore}
                        disabled={savingLore || !newLore.title.trim() || !newLore.content.trim()}
                        className="px-4 py-2 bg-purple-500/90 hover:bg-purple-400 text-white font-semibold rounded-lg transition-all text-sm disabled:opacity-50"
                        data-testid="add-lore-button"
                      >
                        {savingLore ? 'Adding...' : 'Add Lore'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Existing Lore Entries */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-muted-foreground">Existing Lore ({loreEntries.length})</h4>
                </div>
                
                {loreLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading lore...</div>
                ) : loreEntries.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No lore entries yet. Add some lore to enrich your universe!
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {loreEntries.map((entry) => (
                      <div 
                        key={entry._id} 
                        className="p-4 bg-white/5 rounded-lg border border-white/10"
                        data-testid={`lore-entry-${entry._id}`}
                      >
                        {editingLore && editingLore._id === entry._id ? (
                          <div className="space-y-3">
                            <input
                              type="text"
                              value={editingLore.title}
                              onChange={(e) => setEditingLore({ ...editingLore, title: e.target.value })}
                              className="w-full px-3 py-2 bg-white/5 border border-purple-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-foreground text-sm"
                            />
                            <select
                              value={editingLore.category}
                              onChange={(e) => setEditingLore({ ...editingLore, category: e.target.value })}
                              className="px-3 py-2 bg-white/5 border border-purple-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-foreground text-sm"
                            >
                              {LORE_CATEGORIES.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                            <textarea
                              value={editingLore.content}
                              onChange={(e) => setEditingLore({ ...editingLore, content: e.target.value })}
                              rows={3}
                              maxLength={2000}
                              className="w-full px-3 py-2 bg-white/5 border border-purple-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-foreground text-sm resize-none"
                            />
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <label className="flex items-center space-x-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={editingLore.use_in_systems}
                                    onChange={(e) => setEditingLore({ ...editingLore, use_in_systems: e.target.checked })}
                                    className="rounded"
                                  />
                                  <span>Systems</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={editingLore.use_in_planets}
                                    onChange={(e) => setEditingLore({ ...editingLore, use_in_planets: e.target.checked })}
                                    className="rounded"
                                  />
                                  <span>Planets</span>
                                </label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => setEditingLore(null)}
                                  className="px-3 py-1 bg-white/5 hover:bg-white/10 text-foreground rounded text-sm"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleUpdateLore(entry._id)}
                                  disabled={savingLore}
                                  className="px-3 py-1 bg-purple-500/90 hover:bg-purple-400 text-white rounded text-sm disabled:opacity-50"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <div className="flex items-center space-x-2">
                                  <span className="font-semibold">{entry.title}</span>
                                  <span 
                                    className="text-xs px-2 py-0.5 rounded"
                                    style={{ 
                                      backgroundColor: getCategoryColor(entry.category) + '20',
                                      color: getCategoryColor(entry.category)
                                    }}
                                  >
                                    {entry.category}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2 mt-1 text-xs text-muted-foreground">
                                  {entry.use_in_systems && <span className="text-cyan-400">Systems</span>}
                                  {entry.use_in_planets && <span className="text-green-400">Planets</span>}
                                </div>
                              </div>
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => setEditingLore({ ...entry })}
                                  className="p-1 hover:bg-white/10 rounded text-muted-foreground hover:text-foreground"
                                  title="Edit"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDeleteLore(entry._id, entry.title)}
                                  className="p-1 hover:bg-white/10 rounded text-muted-foreground hover:text-destructive"
                                  title="Delete"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2">{entry.content}</p>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-4 p-4 bg-purple-400/10 rounded-lg border border-purple-400/20">
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-muted-foreground">
                    <p className="mb-1"><strong className="text-foreground">How lore works:</strong></p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Lore is included in AI prompts when generating new content</li>
                      <li>Toggle "Systems" for lore to appear in system history generation</li>
                      <li>Toggle "Planets" for lore to appear in planet scan narratives</li>
                      <li>AI will naturally incorporate relevant lore elements</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Lore Preview Modal */}
            {showLorePreview && lorePreview && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowLorePreview(false)}>
                <div className="glass-card rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold">AI Context Preview</h3>
                    <button onClick={() => setShowLorePreview(false)} className="text-muted-foreground hover:text-foreground">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-cyan-400 mb-2">System Generation Context</h4>
                      <pre className="p-3 bg-white/5 rounded-lg text-xs text-muted-foreground whitespace-pre-wrap font-mono">
                        {lorePreview.systems_lore_preview}
                      </pre>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-green-400 mb-2">Planet Scan Context</h4>
                      <pre className="p-3 bg-white/5 rounded-lg text-xs text-muted-foreground whitespace-pre-wrap font-mono">
                        {lorePreview.planets_lore_preview}
                      </pre>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Total lore entries: {lorePreview.total_entries}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setShowLorePreview(false)}
                    className="mt-4 w-full py-2 bg-purple-500/90 hover:bg-purple-400 text-white font-semibold rounded-lg transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {/* Element Value Controls */}
            <div className="glass-card rounded-xl p-6" data-testid="element-value-controls">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold">Element Value Controls</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Adjust credit values per kg based on element rarity. Changes affect all future planet scans.
                  </p>
                </div>
                <button
                  onClick={handleResetAllValues}
                  disabled={resettingValues}
                  className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-cyan-400/20 text-foreground rounded-lg transition-all text-sm disabled:opacity-50"
                  data-testid="reset-values-button"
                >
                  {resettingValues ? 'Resetting...' : 'Reset to Defaults'}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {['Common', 'Uncommon', 'Rare', 'Ultra Rare', 'Legendary', 'Mythic'].map((rarity) => (
                  <div 
                    key={rarity} 
                    className="p-4 bg-white/5 rounded-lg border border-white/10"
                    data-testid={`rarity-${rarity.toLowerCase().replace(' ', '-')}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span 
                        className="font-semibold text-sm"
                        style={{ color: getRarityColor(rarity) }}
                      >
                        {rarity}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Default: {elementValues?.default_rarities?.[rarity] || 0}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={rarityMultipliers[rarity] || 0}
                        onChange={(e) => handleRarityMultiplierChange(rarity, e.target.value)}
                        className="flex-1 px-3 py-2 bg-white/5 border border-cyan-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 text-foreground text-sm"
                        data-testid={`rarity-input-${rarity.toLowerCase().replace(' ', '-')}`}
                      />
                      <button
                        onClick={() => handleSaveRarityMultiplier(rarity)}
                        disabled={savingValues}
                        className="px-3 py-2 bg-cyan-500/90 hover:bg-cyan-400 text-slate-900 font-semibold rounded-lg transition-all text-sm disabled:opacity-50"
                        data-testid={`save-${rarity.toLowerCase().replace(' ', '-')}`}
                      >
                        Save
                      </button>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      credits/kg
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-4 bg-cyan-400/10 rounded-lg border border-cyan-400/20">
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-muted-foreground">
                    <p className="mb-1"><strong className="text-foreground">How values work:</strong></p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Each element has a rarity (Common → Mythic)</li>
                      <li>Planet scans show estimated quantities and total value</li>
                      <li>Value = Quantity (kg) × Credits per kg</li>
                      <li>Changes apply to new scans only</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* User Management */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4">User Management</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Export user data for marketing, analytics, or backup purposes.
              </p>

              <button
                onClick={handleDownloadEmails}
                disabled={downloading}
                className="px-6 py-3 bg-cyan-500/90 hover:bg-cyan-400 text-slate-900 font-semibold rounded-lg btn-glow disabled:opacity-50 transition-all flex items-center space-x-2"
                data-testid="download-emails-button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>{downloading ? 'Downloading...' : 'Download User Emails (CSV)'}</span>
              </button>

              <div className="mt-3 text-xs text-muted-foreground">
                CSV file includes: Email, Credits, Registration Date
              </div>
            </div>

            {/* Danger Zone */}
            <div className="glass-card rounded-xl p-6 border-2 border-destructive/20">
              <h3 className="text-xl font-bold mb-2 text-destructive">Danger Zone</h3>
              <p className="text-sm text-muted-foreground mb-4">
                These actions are irreversible and will affect all users.
              </p>

              <div className="bg-destructive/10 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-destructive mb-2">Reset Universe</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  This will delete all discovered systems and scan data. Users and credits will NOT be affected.
                </p>

                {!confirmReset ? (
                  <button
                    onClick={handleResetUniverse}
                    className="px-6 py-3 bg-destructive hover:bg-destructive/80 text-white font-semibold rounded-lg transition-all"
                    data-testid="reset-button"
                  >
                    Reset Universe
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="p-3 bg-yellow-400/20 border border-yellow-400 rounded-lg">
                      <p className="text-sm font-semibold text-yellow-400">⚠️ Are you absolutely sure?</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        This will delete {stats?.total_systems_discovered || 0} systems and {stats?.total_scans || 0} scans.
                      </p>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleResetUniverse}
                        disabled={resetting}
                        className="px-6 py-3 bg-destructive hover:bg-destructive/80 text-white font-semibold rounded-lg transition-all disabled:opacity-50"
                        data-testid="confirm-reset"
                      >
                        {resetting ? 'Resetting...' : 'Yes, Reset Universe'}
                      </button>
                      <button
                        onClick={() => setConfirmReset(false)}
                        className="px-6 py-3 bg-white/5 hover:bg-white/10 border border-cyan-400/20 text-foreground rounded-lg transition-all"
                        data-testid="cancel-reset"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;
